import { useFolderData } from "@/data/use-folder-data";
import { ALLOWED_FILE_TYPE_EXTENSIONS } from "@/hooks/use-file-collection";
import { MAX_FILESIZE } from "@nnr/global";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

type FileUploadButtonProps = {
  folderUuid: string;
  kitUuid: string;
  groupUuid: string;
  maxFileSize?: number;
};

export type FileUploadDialogRef = {
  openFileDialog: () => void;
};

export const FileUploadDialog = forwardRef<
  FileUploadDialogRef,
  FileUploadButtonProps
>(({ folderUuid, kitUuid, groupUuid, maxFileSize = MAX_FILESIZE }, ref) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<
    { uuid: string | null; file: File }[] | null
  >(null);
  const { createFile, replaceFile, folder, isCreatingFile, isReplacingFile } =
    useFolderData(groupUuid, kitUuid, folderUuid);

  useImperativeHandle(ref, () => ({
    openFileDialog: () => {
      fileInputRef.current?.click();
    },
  }));

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const userSelectedFiles = event.target.files;
    if (!userSelectedFiles || userSelectedFiles.length === 0) return;

    let selectedFiles: { uuid: string | null; file: File }[] = [];
    let rejectedCount = 0;

    for (const file of userSelectedFiles) {
      if (file.size > maxFileSize) {
        rejectedCount++;
      } else {
        selectedFiles.push({
          uuid: folder?.files.find((f) => f.name === file.name)?.uuid ?? null,
          file,
        });
      }
    }

    if (rejectedCount > 0) {
      console.warn(`${rejectedCount} file(s) rejected due to size limit`);
      const toastLabel = new Intl.PluralRules("en-US").select(rejectedCount);
      toast.error(
        `${rejectedCount} file${toastLabel === "one" ? "" : "s"} exceed the maximum size of ${
          maxFileSize / (1024 * 1024)
        } MB and ${toastLabel === "one" ? "was" : "were"} not added.`,
      );
    }

    if (selectedFiles.length === 0) return;
    setSelectedFiles(selectedFiles);
  };

  const handleConfirmUpload = async () => {
    for (const file of selectedFiles!) {
      try {
        if (file.uuid) {
          await replaceFile({ fileUuid: file.uuid, file: file.file });
        } else {
          await createFile(file.file);
        }
        toast.success(`File "${file.file.name}" uploaded successfully!`);
      } catch (error) {
        toast.error(`Failed to upload file "${file.file.name}"`);
        console.error("Failed to upload file:", error);
      }
    }
    setSelectedFiles(null);
  };

  const replaceFiles = selectedFiles?.filter((f) => f.uuid);
  const newFiles = selectedFiles?.filter((f) => !f.uuid);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        style={{ display: "none" }}
        accept={ALLOWED_FILE_TYPE_EXTENSIONS.join(",")}
        multiple
      />
      {selectedFiles && (
        <Dialog
          open={!!selectedFiles}
          onOpenChange={() => setSelectedFiles(null)}
        >
          <DialogContent className="overflow-auto">
            <DialogHeader>
              <DialogTitle>
                Upload File{selectedFiles.length > 1 ? "s" : ""}
              </DialogTitle>
              <DialogDescription>
                <div className="flex flex-col gap-4">
                  <p>
                    Do you want to upload
                    {selectedFiles.length > 1 ? " these files" : " this file"}?
                  </p>
                  <div className="flex flex-col gap-2">
                    {replaceFiles?.length ? (
                      <div className="flex flex-col gap-2">
                        <p>
                          {replaceFiles.length} file
                          {replaceFiles.length > 1 ? "s" : ""} will be replaced:
                        </p>
                        <ul className="flex flex-col gap-2">
                          {replaceFiles.map((file) => (
                            <li
                              className="list-inside list-disc"
                              key={file.file.name}
                            >
                              {file.file.name}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex flex-col gap-2">
                    {newFiles?.length ? (
                      <div className="flex flex-col gap-2">
                        <p>
                          {newFiles.length} new file
                          {newFiles.length > 1 ? "s" : ""} will be added:
                        </p>
                        <ul className="flex flex-col gap-2">
                          {newFiles.map((file) => (
                            <li
                              className="list-inside list-disc"
                              key={file.file.name}
                            >
                              {file.file.name}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedFiles(null)}
                disabled={isCreatingFile || isReplacingFile}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => handleConfirmUpload()}
                disabled={isCreatingFile || isReplacingFile}
              >
                {isCreatingFile || isReplacingFile ? "Uploading..." : "Upload"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
});
